import React, { useState, useEffect } from 'react';
import { UserCheck, Building2, Shield, Search, Users, MapPin } from 'lucide-react';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Input from '../../components/UI/Input';
import { Controller, Region, Office } from '../../lib/sdk/index';
import { useControllers, useRegions, useOffices } from '../../hooks/useQueries';

const useDebounce = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedValue(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

const Controllers: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const limit = 50;
  
  // Use React Query hooks with pagination
  const { data: controllersResponse, isLoading: loading } = useControllers({ 
    page, 
    limit, 
    search: debouncedSearchTerm || undefined 
  });
  
  // Get all regions and offices for stats (without pagination)
  const { data: regionsResponse } = useRegions();
  const { data: officesResponse } = useOffices();
  
  const controllers = Array.isArray(controllersResponse?.data) ? controllersResponse.data : [];
  const pagination = controllersResponse?.pagination;
  const regions = Array.isArray(regionsResponse?.data) ? regionsResponse.data : (Array.isArray(regionsResponse) ? regionsResponse : []);
  const offices = Array.isArray(officesResponse?.data) ? officesResponse.data : (Array.isArray(officesResponse) ? officesResponse : []);
  
  // Reset to page 1 when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Controllers</h1>
        <p className="text-gray-600 dark:text-gray-400">View and search regional and office controllers</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <UserCheck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Controllers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{controllers.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <MapPin className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Regional Controllers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                                 {(() => {
                   const regionsWithControllers = regions.filter(region => {
                     const hasController = region.controllerId && 
                       (typeof region.controllerId === 'string' || 
                        (typeof region.controllerId === 'object' && region.controllerId._id));
                     return hasController;
                   });
                   const regionalControllers = regionsWithControllers.length;
                   return regionalControllers;
                 })()}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
              <Building2 className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Office Controllers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {(() => {
                  const officeControllers = new Set(offices.filter(office => office.controllerId).map(office => 
                    typeof office.controllerId === 'string' ? office.controllerId : office.controllerId._id
                  )).size;
                  return officeControllers;
                })()}
              </p>
            </div>
          </div>
        </div>
      </div>
        
      <div className="card">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <Input
            type="text"
            placeholder="Search controllers by name, CNIC, phone, or father's name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="card">
        {controllers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No controllers found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by adding a new controller in the Manage Controllers page.
            </p>
          </div>
        ) : (
          <>
            <TableRoot>
              <Table>
                <THead>
                  <TR>
                    <TH>Name</TH>
                    <TH>CNIC</TH>
                    <TH>Phone</TH>
                    <TH>Father's Name</TH>
                    <TH>Address</TH>
                  </TR>
                </THead>
                <TBody>
                  {controllers.map((controller) => (
                  <TR key={controller._id}>
                    <TD className="font-medium">{controller.name}</TD>
                    <TD className="font-mono">{controller.cnic}</TD>
                    <TD>{controller.phone}</TD>
                    <TD>{controller.fatherName}</TD>
                    <TD className="max-w-xs truncate" title={controller.address}>
                      {controller.address}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} controllers
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={pagination.page === 1 || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Previous
                </button>
                <span className="flex items-center px-4 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page === pagination.totalPages || loading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
};

export default Controllers;
